import { contentDispositionFilename, LoggerService } from '@adreno-common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CoreService } from '@app/core/services';
import { environment } from '@environments/environment';
import { BehaviorSubject, defer, forkJoin, from, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import {
  ScriptErrorResponse,
  ScriptInputsRequest,
  ScriptInputsResponse,
  ScriptRequests,
  ScriptResponses,
} from '../code';

@Injectable({
  providedIn: 'root',
})
export class ScriptWorkerService {
  private _messageId: number = 0;
  private get messageId() {
    this._messageId++;
    return this._messageId;
  }
  private responses = new Subject<ScriptResponses>();
  private responses$ = this.responses.asObservable();
  private scriptsApi = `${environment.apiPath}/scripts`;
  private workers$ = new BehaviorSubject<{
    [campaignId: number]: { versionId: number; worker?: Worker };
  }>({});

  constructor(
    private logger: LoggerService,
    private http: HttpClient,
    private coreSvc: CoreService
  ) {}

  private downloadScriptVersion(campaignId: number, versionId: number) {
    return this.http
      .get(`${this.scriptsApi}/${campaignId}/${versionId}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map(
          (res) =>
            [
              contentDispositionFilename(res.headers) || 'attachment.txt',
              res.body,
            ] as [string, Blob]
        )
      );
  }

  private onWorkerMessage(response: ScriptResponses) {
    this.logger.debug('ScriptWorkerService', 'onWorkerMessage', response)();
    if (response.type === 'error' && !response.question) {
      //assumed to be inputs request made to a legacy script worker compilation
      this.responses.next({
        messageId: undefined,
        type: 'inputs',
      } as ScriptInputsResponse);
    } else {
      this.responses.next(response);
    }
  }

  private stopWorkers() {
    let workers = this.workers$.value;
    Object.keys(workers).forEach((campaignId) => {
      workers[+campaignId].worker?.terminate();
      delete workers[+campaignId];
    });
    this.workers$.next({});
  }

  inputs$() {
    return this.send$({ type: 'inputs' } as ScriptInputsRequest).pipe(
      map((res) => res as ScriptInputsResponse),
      map((resp) => resp.inputs)
    );
  }

  /*
  loadTestCampaignWorker(campaignId: number, versionId: number) {
    const workers = this.workers$.value;
    const w = workers[campaignId];
    if (!w || w.versionId !== versionId) {
      return this.downloadScriptVersion(campaignId, versionId).pipe(
        tap(([filename, blob]) => {
          const script = URL.createObjectURL(blob);
          const worker = new Worker(script, {
            type: 'classic',
            name: `script/${filename}`,
          });
          worker.onmessage = ({ data }) => this.onWorkerMessage(data);
          this.workers$.next({
            ...workers,
            [campaignId]: { versionId, worker },
          });
        }),
        map(() => undefined)
      );
    } else {
      return of(undefined);
    }
  }
  */

  loadWorkers(campaigns: { campaignId: number; versionId: number }[]) {
    let workers = this.workers$.value;
    forkJoin(
      campaigns
        .filter((c) => {
          const w = workers[c.campaignId];
          return !w || w.versionId !== c.versionId;
        })
        .map((c) =>
          this.downloadScriptVersion(c.campaignId, c.versionId).pipe(
            map(([filename, blob]) => {
              const worker = new Worker(URL.createObjectURL(blob), {
                type: 'classic',
                name: `script/${filename}`,
              });
              worker.onmessage = ({ data }) => this.onWorkerMessage(data);
              return { [c.campaignId]: { versionId: c.versionId, worker } };
            })
          )
        )
    ).subscribe((newWorkers) => {
      const nwReduced = newWorkers.reduce(
        (prev, curr) => ({ ...prev, ...curr }),
        {}
      );
      this.workers$.next({
        ...workers,
        ...nwReduced,
      });
    });
  }

  send$(req: ScriptRequests) {
    return defer(() => {
      req = { ...req, messageId: this.messageId };
      this.logger.debug('ScriptWorkerService', 'send$', req)();
      return this.coreSvc.campaignId$.pipe(
        filter((campaignId) => !!campaignId && campaignId > 0),
        map((campaignId) => campaignId!),
        switchMap((campaignId) =>
          this.workers$.pipe(
            filter((workers) => !!workers[campaignId]?.worker),
            map((workers) => workers[campaignId]!.worker!)
          )
        ),
        take(1),
        tap((worker) => worker.postMessage(req)),
        switchMap(() =>
          this.responses$.pipe(
            filter(
              (resp) =>
                resp?.messageId === req.messageId ||
                (req.type === 'inputs' && !resp.messageId)
            ),
            take(1)
          )
        )
      );
    });
  }
}
